import { FieldLabel } from "../shared/FieldLabel";
import { useState, type FormEvent } from "react";
import { useManualDraftGuard, type RegisterBeforeLeave } from "../shared/useManualDraftGuard";
import { PROJECT_TYPES, type ProjectInput, type ProjectStrategy, type ProjectType } from "../../domain/project";
import type { ProjectStatus } from "../../domain/status";
import { centsToInputString, parseMoneyInput } from "../../domain/money";
import { parseDecimalX100, x100ToInputString } from "../../domain/measurement";

interface ProjectFormProps {
  registerBeforeLeave?: RegisterBeforeLeave;
  title: string;
  statuses: ProjectStatus[];
  /** Spec §90: the workspace's renovation standards, newest default first. */
  standards?: Array<{ id: string; name: string; isDefault: boolean }>;
  /** Which standard the new project should start from (null = none). */
  standardId?: string | null;
  onStandardChange?: (standardId: string | null) => void;
  initial: ProjectInput;
  submitLabel: string;
  busy: boolean;
  /** Problems reported by the caller (e.g. persistence failures). */
  externalError: string | null;
  onSubmit: (input: ProjectInput) => void;
  onCancel: () => void;
  /**
   * Spec §86: creating a project asks in the order a person thinks about a
   * property — where it is, what it cost, what it is, what the plan is — and
   * leaves status (Planning) and notes out of the way. Editing shows
   * everything.
   */
  mode?: "create" | "edit";
}

/**
 * Shared create/edit project form. Money fields are edited as text and
 * parsed into integer cents on submit (never floating point).
 *
 * Only the ADDRESS or a NAME is needed to begin. Everything else is labelled
 * optional because it is: a person evaluating a house often does not know
 * the price yet, and a project that exists is more useful than a form
 * abandoned for want of a number.
 */
export function ProjectForm(props: ProjectFormProps) {
  const { title, statuses, initial, submitLabel, busy, externalError, onSubmit, onCancel, standards, standardId, onStandardChange } = props;
  const creating = props.mode === "create";

  const [name, setName] = useState(initial.name);
  // A name nobody typed follows the street address until somebody does.
  const [nameTouched, setNameTouched] = useState(initial.name.trim() !== "");
  const [statusId, setStatusId] = useState(initial.statusId);
  const [projectType, setProjectType] = useState<ProjectType>(initial.projectType ?? "renovation");
  const [strategy, setStrategy] = useState<ProjectStrategy>(initial.strategy ?? "flip");
  const [streetAddress, setStreetAddress] = useState(initial.streetAddress);
  const [city, setCity] = useState(initial.city);
  const [state, setState] = useState(initial.state);
  const [zip, setZip] = useState(initial.zip);
  const [purchasePrice, setPurchasePrice] = useState(centsToInputString(initial.purchasePriceCents));
  const [expectedArv, setExpectedArv] = useState(centsToInputString(initial.expectedArvCents));
  const [livingArea, setLivingArea] = useState(x100ToInputString(initial.livingAreaSqftX100 ?? null));
  const [notes, setNotes] = useState(initial.notes);
  const [problems, setProblems] = useState<string[]>([]);
  useManualDraftGuard({ name, nameTouched, statusId, projectType, strategy, streetAddress, city, state, zip, purchasePrice, expectedArv, livingArea, notes, standardId }, "project", props.registerBeforeLeave);

  const effectiveName = nameTouched ? name : streetAddress;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const found: string[] = [];

    if (effectiveName.trim() === "") {
      found.push(creating ? "Enter the property's street address (or a name for the project)." : "Project name is required.");
    }

    const purchaseResult = parseMoneyInput(purchasePrice);
    if (!purchaseResult.ok) found.push(`Purchase price: ${purchaseResult.error}`);
    const arvResult = parseMoneyInput(expectedArv);
    if (!arvResult.ok) found.push(`Value after renovation: ${arvResult.error}`);
    const areaResult = parseDecimalX100(livingArea);
    if (!areaResult.ok) found.push(`Living area: ${areaResult.error}`);

    if (found.length > 0 || !purchaseResult.ok || !arvResult.ok || !areaResult.ok) {
      setProblems(found);
      const field = effectiveName.trim() === "" ? "projectName" : !purchaseResult.ok ? "purchasePrice" : !arvResult.ok ? "expectedArv" : "livingArea";
      event.currentTarget.querySelector<HTMLInputElement>(`input[name="${field}"]`)?.focus();
      return;
    }
    setProblems([]);

    onSubmit({
      name: effectiveName,
      projectType,
      statusId,
      streetAddress,
      city,
      state,
      zip,
      purchasePriceCents: purchaseResult.cents,
      expectedArvCents: arvResult.cents,
      notes,
      strategy,
      livingAreaSqftX100: areaResult.valueX100,
    });
  }

  const allProblems = externalError ? [...problems, externalError] : problems;
  const optional = <span className="muted form-optional">optional</span>;

  return (
    <form className="panel form-panel project-form" onSubmit={handleSubmit}>
      <h2>{title}</h2>
      {creating && (
        <p className="muted workspace-note">
          Enter a street address or project name to start. Anything else you do not know yet can be added later.
        </p>
      )}

      {allProblems.length > 0 && (
        <div className="error-panel" role="alert">
          <ul>
            {allProblems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </div>
      )}

      {creating && standards && standards.length > 0 && onStandardChange && (
        <fieldset disabled={busy}>
          <h3>Your usual?</h3>
          <div className="form-grid">
            <FieldLabel>
              Renovation standard
              <select value={standardId ?? ""} onChange={(e) => onStandardChange(e.target.value || null)}>
                {standards.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.isDefault ? " (default)" : ""}
                  </option>
                ))}
                <option value="">No standard — choose everything on this project</option>
              </select>
            </FieldLabel>
          </div>
          <p className="muted small">
            The project starts from this standard's finishes, packages and prices, and takes its own copy: editing the standard later
            never changes this project.
          </p>
        </fieldset>
      )}

      <fieldset disabled={busy}>
        <h3>{creating ? "1. The property" : "Property"}</h3>
        <div className="form-grid">
          <FieldLabel className="span-2" required={creating && (!nameTouched || name.trim() === "")}>
            Street address
            <input
              value={streetAddress}
              onChange={(e) => setStreetAddress(e.target.value)}
              placeholder="e.g. 2006 N Palace Ave"
              autoComplete="street-address"
              autoFocus={creating}
            />
          </FieldLabel>
          <FieldLabel>
            City
            <input value={city} onChange={(e) => setCity(e.target.value)} autoComplete="address-level2" />
          </FieldLabel>
          <FieldLabel>
            State
            <input value={state} onChange={(e) => setState(e.target.value)} placeholder="e.g. TX" autoComplete="address-level1" />
          </FieldLabel>
          <FieldLabel>
            ZIP code
            <input value={zip} onChange={(e) => setZip(e.target.value)} inputMode="numeric" autoComplete="postal-code" />
          </FieldLabel>
          <FieldLabel required={!creating || nameTouched || streetAddress.trim() === ""} error={problems.find(p=>p.includes("Project name")||p.includes("street address"))}>
            <span className="field-name">Project name {creating && !nameTouched && streetAddress.trim() !== "" && optional}</span>
            <input
              name="projectName"
              value={effectiveName}
              onChange={(e) => {
                setName(e.target.value);
                setNameTouched(true);
              }}
              placeholder={creating ? "Uses the street address" : "e.g. 2006 N Palace Ave"}
              autoFocus={!creating}
            />
          </FieldLabel>
        </div>

        <h3>{creating ? "2. The purchase" : "Purchase"}</h3>
        <div className="form-grid">
          <FieldLabel error={problems.find(p=>p.startsWith("Purchase price:"))}>
            <span className="field-name">Purchase price {optional}</span>
            <input
              name="purchasePrice"
              value={purchasePrice}
              onChange={(e) => setPurchasePrice(e.target.value)}
              placeholder="e.g. 145,000"
              inputMode="decimal"
            />
          </FieldLabel>
          <FieldLabel error={problems.find(p=>p.startsWith("Value after renovation:"))}>
            <span className="field-name">Value after renovation (ARV) {optional}</span>
            <input
              name="expectedArv"
              value={expectedArv}
              onChange={(e) => setExpectedArv(e.target.value)}
              placeholder="e.g. 239,000"
              inputMode="decimal"
            />
          </FieldLabel>
        </div>

        <h3>{creating ? "3. About the house" : "About the house"}</h3>
        <div className="form-grid">
          <FieldLabel error={problems.find(p=>p.startsWith("Living area:"))}>
            <span className="field-name">Living area (sq ft) {optional}</span>
            <input
              name="livingArea"
              value={livingArea}
              onChange={(e) => setLivingArea(e.target.value)}
              placeholder="e.g. 1,650"
              inputMode="decimal"
            />
          </FieldLabel>
          <FieldLabel>
            Kind of project
            <select
              value={projectType}
              onChange={(e) => setProjectType(e.target.value as ProjectType)}
              title="Renovation starts from the house as it is today; New Construction plans a building from nothing."
            >
              {PROJECT_TYPES.map((t) => (
                <option key={t.id} value={t.id}>{t.displayName}</option>
              ))}
            </select>
          </FieldLabel>
        </div>

        <h3>{creating ? "4. The plan for it" : "Plan"}</h3>
        <div className="choice-row" role="radiogroup" aria-label="The plan for this property">
          <FieldLabel className={`choice-card ${strategy === "flip" ? "choice-card-selected" : ""}`}>
            <input type="radio" name="strategy" checked={strategy === "flip"} onChange={() => setStrategy("flip")} />
            <strong>Sell after renovating</strong>
            <span className="muted">A flip — Berrier Reno forecasts the sale and profit.</span>
          </FieldLabel>
          <FieldLabel className={`choice-card ${strategy === "rental" ? "choice-card-selected" : ""}`}>
            <input type="radio" name="strategy" checked={strategy === "rental"} onChange={() => setStrategy("rental")} />
            <strong>Keep and rent</strong>
            <span className="muted">A rental — Berrier Reno shows rent, cash flow and value instead.</span>
          </FieldLabel>
        </div>
        <p className="muted form-hint">Not decided? Pick either — you can switch any time and nothing is lost.</p>

        {!creating && (
          <>
            <h3>Status</h3>
            <div className="form-grid">
              <FieldLabel>
                Status
                <select value={statusId} onChange={(e) => setStatusId(e.target.value)}>
                  {statuses.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.displayName}
                    </option>
                  ))}
                </select>
              </FieldLabel>
            </div>
          </>
        )}

        {creating ? (
          <details className="form-more">
            <summary>Notes {optional}</summary>
            <FieldLabel className="notes-label">
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Anything worth remembering" />
            </FieldLabel>
          </details>
        ) : (
          <>
            <h3>Notes</h3>
            <FieldLabel className="notes-label">
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Optional project notes" />
            </FieldLabel>
          </>
        )}
      </fieldset>

      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? "Saving…" : submitLabel}
        </button>
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function emptyProjectInput(defaultStatusId: string, strategy: ProjectStrategy = "flip"): ProjectInput {
  return {
    name: "",
    statusId: defaultStatusId,
    streetAddress: "",
    city: "",
    state: "",
    zip: "",
    purchasePriceCents: null,
    expectedArvCents: null,
    notes: "",
    strategy,
    livingAreaSqftX100: null,
  };
}
